import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

export type BatchItemDraft = {
  externalId: string
  name?: string
  document?: string
  pixKey: string
  amount: number
  errors: string[]
}

const ALIASES: Record<keyof Omit<BatchItemDraft, 'errors'>, string[]> = {
  name: ['nome', 'name'],
  document: ['documento', 'document', 'cpf', 'cnpj', 'cpf_cnpj'],
  pixKey: ['chave_pix', 'chavepix', 'chave', 'pix', 'pixkey'],
  amount: ['valor', 'amount', 'value'],
  externalId: ['identificador', 'id_unico', 'idunico', 'id', 'external_id', 'externalid'],
}

const normalize = (h: string) =>
  String(h)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '_')

const pick = (record: Record<string, any>, keys: string[]) => {
  for (const k of Object.keys(record)) {
    if (keys.includes(normalize(k))) return record[k]
  }
  return undefined
}

export function mapRows(records: Record<string, any>[]): BatchItemDraft[] {
  const seen = new Set<string>()
  return records.map((rec) => {
    const externalId = String(pick(rec, ALIASES.externalId) ?? '').trim()
    const pixKey = String(pick(rec, ALIASES.pixKey) ?? '').trim()
    const name = String(pick(rec, ALIASES.name) ?? '').trim() || undefined
    const document = String(pick(rec, ALIASES.document) ?? '').trim() || undefined
    const amount = parseBrCurrency(pick(rec, ALIASES.amount))
    const errors: string[] = []
    if (!externalId) errors.push('id_unico é obrigatório')
    else if (seen.has(externalId)) errors.push('id_unico duplicado')
    seen.add(externalId)
    if (!pixKey) errors.push('chave Pix é obrigatória')
    if (amount === null || !(amount > 0)) errors.push('valor inválido')
    return { externalId, name, document, pixKey, amount: amount ?? 0, errors }
  })
}

// Parser de CSV para o layout do modelo: separador ';', cabeçalho na 1ª linha,
// com suporte a aspas duplas básicas. Evita o papaparse, cujo campo "browser"
// aponta para o papaparse.min.js (linha única) e estoura a pilha no build do CRA.
function splitCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"'
        i++
      } else {
        inQuotes = !inQuotes
      }
    } else if (c === ';' && !inQuotes) {
      out.push(cur)
      cur = ''
    } else {
      cur += c
    }
  }
  out.push(cur)
  return out
}

export function parseCsv(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0)
  if (lines.length === 0) return []
  const headers = splitCsvLine(lines[0]).map((h) => h.trim())
  return lines.slice(1).map((line) => {
    const cells = splitCsvLine(line)
    const rec: Record<string, string> = {}
    headers.forEach((h, i) => {
      rec[h] = (cells[i] ?? '').trim()
    })
    return rec
  })
}

export async function parseFile(file: File): Promise<BatchItemDraft[]> {
  const isExcel = /\.(xlsx|xls)$/i.test(file.name)
  let records: Record<string, any>[]
  if (isExcel) {
    const buffer = await file.arrayBuffer()
    const wb = XLSX.read(buffer, { type: 'array' })
    const sheet = wb.Sheets[wb.SheetNames[0]]
    records = XLSX.utils.sheet_to_json(sheet, { defval: '' })
  } else {
    const text = await file.text()
    records = parseCsv(text)
  }
  return mapRows(records)
}

export function downloadTemplate() {
  const csv = 'nome;documento;chave_pix;valor;identificador\nFulano de Tal;12345678900;fulano@email.com;1.234,56;PG-0001\n'
  saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'modelo-saque-lote.csv')
}

export function parseBrCurrency(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined) return null
  if (typeof input === 'number') return Number.isFinite(input) ? input : null
  let s = String(input)
    .trim()
    .replace(/r\$/gi, '')
    .replace(/\s/g, '')
  if (!s) return null
  const hasComma = s.includes(',')
  const hasDot = s.includes('.')
  if (hasComma && hasDot) {
    // BR: ponto é separador de milhar, vírgula é decimal
    s = s.replace(/\./g, '').replace(',', '.')
  } else if (hasComma) {
    s = s.replace(',', '.')
  }
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}
